import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../../../ui/sheet";
import { Button } from "../../../../ui/button";
import { Input } from "../../../../ui/input";
import { Label } from "../../../../ui/label";
import { Badge } from "../../../../ui/badge";
import { Sparkles, Loader2, Save, Send } from "lucide-react";
import { RichEmailEditor } from "../editor/RichEmailEditor";
import { epicengageSupabasev1 } from "../../../../../integrations/supabase/client";
import { useMerchant } from "../../../../../hooks/useMerchantEngageV1";
import { useToast } from "../../../../../hooks/use-toast";

export type DraftSeed = {
  mode: "event";
  event_id: string | null;
  event_name: string;
  event_date: string;
  extra_context?: string;
};

type DraftJson = {
  subject_line: string;
  body_html: string;
  body_plain: string;
  cta_text: string;
  cta_url_placeholder: string;
};

export const AiDraftPanel = ({
  seed,
  onClose,
  onSent,
}: {
  seed: DraftSeed | null;
  onClose: () => void;
  onSent: (templateId: string) => void;
}) => {
  const { merchant } = useMerchant();
  const { toast } = useToast();
  const open = !!seed;

  const [generating, setGenerating] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [subject, setSubject] = useState("");
  const [ctaText, setCtaText] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [bodyPlain, setBodyPlain] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);


  useEffect(() => {
    if (!open) {
      setSubject("");
      setCtaText("");
      setCtaUrl("");
      setBodyHtml("");
      setBodyPlain("");
      setStreamText("");
      setError(null);
    }
  }, [open]);

  const generate = async () => {
    if (!seed || !merchant) return;
    setGenerating(true);
    setError(null);
    setStreamText("");
    setSubject("");
    setCtaText("");
    setCtaUrl("");
    setBodyHtml("");
    setBodyPlain("");

    try {
      const { data: sessionData } = await epicengageSupabasev1.auth.getSession();
      const token = sessionData.session?.access_token;
      const url = `${process.env.REACT_APP_SUPABASE_ENGAGEV1_URL}/functions/v1/ai-campaign-draft`;
      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          mode: "event",
          business_type: merchant.business_type || "small business",
          brand_tone: merchant.brand_tone || "Friendly & Casual",
          event_name: seed.event_name + (seed.extra_context ? ` — ${seed.extra_context}` : ""),
          event_date: seed.event_date,
          max_discount_pct: 20,
        }),
      });

      if (!resp.ok || !resp.body) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";
      let assembled = "";
      let done = false;
      while (!done) {
        const { done: d, value } = await reader.read();
        if (d) break;
        textBuffer += decoder.decode(value, { stream: true });
        let idx: number;
        while ((idx = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, idx);
          textBuffer = textBuffer.slice(idx + 1);
          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (!line.startsWith("data: ")) continue;
          const json = line.slice(6).trim();
          if (json === "[DONE]") {
            done = true;
            break;
          }
          try {
            const parsed = JSON.parse(json);
            const c = parsed.choices?.[0]?.delta?.content;
            if (c) {
              assembled += c;
              setStreamText(assembled);
            }
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }

      const cleaned = assembled.replace(/```json|```/g, "").trim();
      const draft: DraftJson = JSON.parse(cleaned);
      setSubject(draft.subject_line || "");
      setCtaText(draft.cta_text || "");
      setCtaUrl(draft.cta_url_placeholder || "");
      setBodyPlain(draft.body_plain || "");
      setBodyHtml(draft.body_html || "");
      setStreamText("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to generate");
    } finally {
      setGenerating(false);
    }
  };

  const saveTemplate = async (sendNow: boolean) => {
    if (!seed || !merchant) return;
    if (!subject.trim() || !bodyHtml) {
      toast({ title: "Missing fields", description: "Subject and body are required.", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const { data, error: insertErr } = await epicengageSupabasev1
        .from("email_templates")
        .insert({
          merchant_id: merchant.id,
          name: seed.event_name,
          type: "event",
          subject_line: subject,
          body_html: bodyHtml,
          body_plain: bodyPlain || bodyHtml.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim(),
          cta_text: ctaText || null,
          cta_url: ctaUrl || null,
          ai_generated: true,
          event_id: seed.event_id,
        })
        .select("id")
        .single();
      if (insertErr) throw insertErr;
      toast({ title: sendNow ? "Template ready" : "Template saved" });
      if (sendNow) {
        onSent(data.id);
      } else {
        onClose();
      }
    } catch (e: any) {
      console.error("Save template error:", e);
      toast({
        title: "Save failed",
        description: e?.message || e?.details || e?.hint || "Unknown error",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI Campaign Draft
          </SheetTitle>
          <SheetDescription>
            {seed?.event_name}
            {seed?.event_date && ` · ${new Date(seed.event_date).toLocaleDateString("en-SG", { month: "short", day: "numeric", year: "numeric" })}`}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground space-y-1">
          <div>
            <span className="font-medium text-foreground">Business type:</span>{" "}
            {merchant?.business_type || "—"}
          </div>
          <div>
            <span className="font-medium text-foreground">Brand tone:</span>{" "}
            {merchant?.brand_tone || "—"}
          </div>
        </div>

        {!subject && !generating && (
          <div className="mt-6">
            <Button onClick={generate} className="w-full" disabled={!merchant}>
              <Sparkles className="h-4 w-4 mr-2" />
              Generate Draft
            </Button>
          </div>
        )}

        {generating && (
          <div className="mt-6 space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Generating with AI…
            </div>
            {streamText && (
              <pre className="text-xs bg-muted/30 rounded p-3 max-h-40 overflow-y-auto whitespace-pre-wrap font-mono">
                {streamText.slice(-500)}
              </pre>
            )}
          </div>
        )}

        {error && (
          <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
            <Button variant="outline" size="sm" className="mt-2" onClick={generate}>
              Try again
            </Button>
          </div>
        )}

        {subject && !generating && (
          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="subject">Subject line</Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Body</Label>
              <div className="mt-1">
                <RichEmailEditor value={bodyHtml} onChange={setBodyHtml} minHeight={400} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cta-text">CTA text</Label>
                <Input
                  id="cta-text"
                  value={ctaText}
                  onChange={(e) => setCtaText(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="cta-url">CTA URL</Label>
                <Input
                  id="cta-url"
                  value={ctaUrl}
                  onChange={(e) => setCtaUrl(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <Button variant="outline" onClick={generate} disabled={generating}>
                <Sparkles className="h-4 w-4 mr-2" /> Regenerate
              </Button>
              <div className="flex-1" />
              <Button variant="secondary" onClick={() => saveTemplate(false)} disabled={saving}>
                <Save className="h-4 w-4 mr-2" /> Save as Template
              </Button>
              <Button onClick={() => saveTemplate(true)} disabled={saving}>
                <Send className="h-4 w-4 mr-2" /> Send Now
              </Button>
            </div>

            <Badge variant="outline" className="text-xs">
              AI generated · edit freely before saving
            </Badge>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
